import { useMemo } from 'react';

import { useParams } from '@redwoodjs/router';
import { useQuery } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import { ACCOUNT_STATEMENT } from 'src/graphql/queries/accountStatement.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { stringToCamelCase } from 'src/lib/formatters';
import formatCentsToLocaleCurrency from 'src/utils/finance';
import { formatDate, onError } from 'src/utils/utils';

import StatementBillingDetails from './StatementBillingDetails';
import StatementDetails from './StatementDetails';
import StatementItemTable from './StatementItemTable';
import StatementTotals from './StatementTotals';
import TableKey from './TableKey';
import { AccountStatementItemType } from './types';

function ViewAccountStatementPage() {
    const { id } = useParams();

    const { data, loading } = useQuery(ACCOUNT_STATEMENT, {
        variables: {
            statementId: parseInt(id),
        },
        fetchPolicy: 'cache-and-network',
        onError,
    });

    const statementData = useMemo(() => {
        const getAccountStatement = data?.getAccountStatement ?? {};
        const lang = navigator.language;
        let balanceLabel = '';
        let lastItemId = 0;
        const accountStatementItems =
            getAccountStatement.accountStatementItems?.map((item) => {
                let credit = '';
                let debit = '';

                if (item.itemType === AccountStatementItemType.BALANCE) {
                    balanceLabel = stringToCamelCase(item.description);
                }

                if (item.credit) {
                    credit = formatCentsToLocaleCurrency(item.credit, lang);
                } else if (item.debit) {
                    debit = formatCentsToLocaleCurrency(item.debit, lang);
                }

                lastItemId = item.id > lastItemId ? item.id : lastItemId;

                return {
                    ...item,
                    transactionDate: formatDate({ date: new Date(item.transactionDate) }),
                    credit,
                    debit,
                    rollingBalance: formatCentsToLocaleCurrency(item.rollingBalance, lang),
                };
            }) ?? [];

        const {
            outstandingBalance,
            balanceBroughtForward,
            totalInvoiceAmount,
            totalCreditApplied,
            totalPaymentsMade,
            accountStatementId,
            startDateTime,
            endDateTime,
            statementDate,
        } = getAccountStatement ?? {};

        const totals = {
            outstandingBalance: formatCentsToLocaleCurrency(outstandingBalance, lang),
            balanceBroughtForward: formatCentsToLocaleCurrency(balanceBroughtForward, lang),
            totalInvoiceAmount: formatCentsToLocaleCurrency(totalInvoiceAmount, lang),
            totalCreditApplied: formatCentsToLocaleCurrency(totalCreditApplied, lang),
            totalPaymentsMade: formatCentsToLocaleCurrency(totalPaymentsMade, lang),
            balanceLabel,
        };

        const statementDetails = {
            statementId: accountStatementId,
            startDate: formatDate({ date: new Date(startDateTime) }),
            endDate: formatDate({ date: new Date(endDateTime) }),
            statementDate: formatDate({ date: new Date(statementDate) }),
        };

        return { ...getAccountStatement, totals, accountStatementItems, statementDetails };
    }, [data]);

    return (
        <PageContent heading="View Account Statement">
            <Flex flexDirection="column" gap="24px">
                <Flex gap="inherit">
                    <Flex flexDirection="column" flex={1} gap="24px">
                        <StatementDetails {...statementData.statementDetails} />
                    </Flex>
                    <Flex flexDirection="column" flex={2} gap="24px">
                        <StatementBillingDetails {...statementData.merchant} />
                    </Flex>
                </Flex>
                <Flex flexDirection="column" gap="24px">
                    <StatementTotals {...statementData.totals} />
                </Flex>
                <Flex flexDirection="column" gap="24px">
                    <Flex flexDirection="column" flex={1} gap="24px">
                        <TableKey />
                    </Flex>
                    <Flex>
                        <StatementItemTable
                            loading={loading}
                            tableData={statementData.accountStatementItems}
                        />
                    </Flex>
                </Flex>
            </Flex>
        </PageContent>
    );
}

export default ViewAccountStatementPage;
